/* eslint-disable indent */
import { useEffect, useState, useContext } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  TextField,
  Alert,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { AuthContext } from 'utils/AuthContext';
import { pharmacyDetailsSchema } from 'utils/schema';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { getPharmacyDetails, savePharmacyDetails } from 'store/slices/userSlice';
import { toast } from 'react-toastify';
import { ROUTE } from 'route/RouteEnums';
import { useLocation, useNavigate } from 'react-router-dom';
import ReactPhoneInput from 'react-phone-input-material-ui';
import { YES, permissions } from 'utils/dropDownList';
import { Restricted } from 'components/Restricted';

const PharmacyDetails = (props: any) => {
  const [authorizationMsg, setAuthorizationMsg] = useState<boolean>(false);
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(pharmacyDetailsSchema),
    mode: 'all',
  });
  const isProfileDetailsFetched = useAppSelector(
    (state) => state.user.loaders.isProfileDetailsFetched
  );
  const { user } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state } = useLocation();

  useEffect(() => {
    if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
      fetchPharmacyDetails(
        props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId
      );
    } else {
      const message = 'Please add patient details first.';
      toast(message, {
        type: 'info',
        icon: true,
      });
      navigate(ROUTE.PATIENT_INFORMATION);
    }
    //eslint-disable-next-line
  }, [dispatch, setValue, user?.patientId]);

  const fetchPharmacyDetails = (patientId: any) => {
    dispatch(getPharmacyDetails(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200 && response?.data !== '') {
          Object.keys(response?.data).forEach((key: any) => {
            switch (key) {
              case 'accessMedicationHistory':
                setValue(
                  'accessMedicationHistory',
                  response?.data?.accessMedicationHistory !== null
                    ? response?.data?.accessMedicationHistory
                      ? 'true'
                      : 'false'
                    : '',
                  {
                    shouldValidate: true,
                    shouldDirty: false,
                  }
                );
                if (
                  !response?.data?.accessMedicationHistory &&
                  response?.data?.accessMedicationHistory !== null
                )
                  setAuthorizationMsg(!response?.data?.accessMedicationHistory);
                break;
              case 'phoneNumber':
                if (response?.data[key].length === 10) setValue(key, `1${response?.data[key]}`);
                else setValue(key, response?.data[key]);
                break;
              default:
                setValue(key, response.data[key], { shouldValidate: true, shouldDirty: false });
            }
          });
        }
      });
  };

  const onSubmitForm = (formData: any) => {
    if (isValid && isDirty) {
      const payload = {
        ...formData,
        phoneNumber: formData?.phoneNumber.slice(1),
        primaryPharmacy: 'No',
      };
      if (props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId) {
        dispatch(
          savePharmacyDetails({
            patientId:
              props?.from === 'admin' ? state?.patientid || props?.patientId : user?.patientId,
            payload: payload,
          })
        )
          .unwrap()
          .then((res: any) => {
            if (res.status === 200) {
              toast('Pharmacy details added successfully', {
                type: 'success',
                icon: true,
              });
              reset(formData);
              props?.onComplete();
            }
          });
      } else {
        const message = 'Please add patient details first.';
        toast(message, {
          type: 'info',
          icon: true,
        });
        navigate(ROUTE.PATIENT_INFORMATION);
      }
    } else {
      toast('No changes to update.', {
        type: 'info',
        icon: true,
      });
    }
  };

  return (
    <Container component='main' maxWidth='sm'>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          component='form'
          noValidate
          sx={{ mt: 1 }}
          onSubmit={handleSubmit((formData) => onSubmitForm(formData))}
        >
          <Grid container spacing={2}>
            <Typography sx={{ m: '15px 0 0 15px' }}>
              Enter your pharmacy details to ensure seamless medication refills.
            </Typography>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='pharmacyName'>
                {props?.from !== 'admin' ? 'Pharmacy Name*' : 'Pharmacy Name'}
              </InputLabel>
              <TextField
                required
                fullWidth
                placeholder="Enter your pharmacy's name"
                id='pharmacyName'
                autoComplete='pharmacyName'
                {...register('pharmacyName')}
                error={!!errors.pharmacyName}
                helperText={errors?.pharmacyName?.message}
                disabled={!user?.permissions.includes('EDIT_PROFILE')}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='phoneNumber'>
                {props?.from !== 'admin' ? 'Pharmacy’s  Phone number*' : 'Pharmacy’s  Phone number'}
              </InputLabel>
              <ReactPhoneInput
                component={TextField}
                label=''
                onlyCountries={['us']}
                country={'us'}
                masks={{ us: '(...) ...-....' }}
                autoFormat={true}
                disableDropdown={true}
                defaultErrorMessage={'Please enter a Pharmacy’s  phone number'}
                placeholder='+1 (555) 000-0000'
                {...register('phoneNumber')}
                inputProps={{
                  helperText: errors.phoneNumber ? errors.phoneNumber?.message : '',
                  error: !!errors.phoneNumber,
                }}
                countryCodeEditable={false}
                onChange={(e) => {
                  setValue('phoneNumber', e, { shouldValidate: true, shouldDirty: true });
                }}
                value={watch('phoneNumber') ? watch('phoneNumber') : null}
                disabled={!user?.permissions.includes('EDIT_PROFILE')}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='address'>Pharmacy&apos;s Address*</InputLabel>
              <TextField
                fullWidth
                multiline
                rows={6}
                placeholder="Enter pharmacy's address..."
                id='address'
                autoComplete='address'
                variant='filled'
                {...register('address')}
                error={!!errors.address}
                helperText={errors.address ? errors.address?.message : ''}
                value={watch('address')}
                disabled={!user?.permissions.includes('EDIT_PROFILE')}
                inputProps={{
                  maxLength: 255,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <InputLabel htmlFor='ZIP-code'>
                {props?.from !== 'admin' ? 'ZIP Code*' : 'ZIP Code'}
              </InputLabel>
              <TextField
                fullWidth
                id='where-are-you-located?'
                autoComplete='zip-code'
                placeholder='ZIP Code'
                {...register('zipCode')}
                error={!!errors.zipCode}
                helperText={errors?.zipCode ? errors?.zipCode?.message : ''}
                value={watch('zipCode')}
                disabled={!user?.permissions.includes('EDIT_PROFILE')}
                inputProps={{
                  maxLength: 5,
                  pattern: '[0-9]{5}',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel htmlFor='accessMedicationHistory'>
                Do you authorize us to obtain medication history?*
              </InputLabel>
              <FormControl error={!!errors.accessMedicationHistory}>
                <RadioGroup
                  name='accessMedicationHistory'
                  value={watch('accessMedicationHistory') || ''}
                  onChange={(e) => {
                    const value = e.target.value;

                    setValue('accessMedicationHistory', value, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });

                    // ✅ Update alert logic
                    if (value === 'true') {
                      setAuthorizationMsg(false);
                    } else if (value === 'false') {
                      setAuthorizationMsg(true);
                    }
                  }}
                >
                  <FormControlLabel
                    value='true'
                    control={<Radio />}
                    label='Yes'
                    disabled={!user?.permissions.includes('EDIT_PROFILE')}
                  />
                  <FormControlLabel
                    value='false'
                    control={<Radio />}
                    label='No'
                    disabled={!user?.permissions.includes('EDIT_PROFILE')}
                  />
                </RadioGroup>
                {errors.accessMedicationHistory && (
                  <FormHelperText>{errors.accessMedicationHistory?.message}</FormHelperText>
                )}
              </FormControl>
              {watch('accessMedicationHistory') === 'false' && (
                <Alert severity='error'>
                  Without authorization, we can’t access your pharmacy records, which helps us
                  provide accurate care. Please consider allowing access.
                </Alert>
              )}
            </Grid>

            <Restricted permission={permissions.EDIT_PROFILE}>
              <Grid item xs={12} display={'flex'} justifyContent={'center'}>
                <LoadingButton
                  loading={isProfileDetailsFetched}
                  variant='contained'
                  type='submit'
                  sx={{
                    minWidth: 140,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  Save
                </LoadingButton>
              </Grid>
            </Restricted>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default PharmacyDetails;
